import { AppProvider } from "@shopify/polaris";
import "@shopify/polaris/build/esm/styles.css";
import enTranslations from "@shopify/polaris/locales/en.json";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import JSConfetti from "js-confetti";
// Direct import to avoid barrel export - prevents pulling Preview (2.6MB) and other heavy components
import App from "./components/App";
import "./styles/drag-drop.css";
import "./styles/calendar-modal.css";

// Console.log deactivation for production
// Secure debug mode detection with environment awareness
const isDebugForced = (() => {
  // In production, never allow debug mode
  if (import.meta.env.PROD) {
    return false;
  }
  
  // In development, check multiple sources
  try {
    // 1. Check localStorage (persists across sessions)
    const storageDebug = localStorage.getItem('nudgesmart_debug');
    if (storageDebug === 'true') return true;
    
    // 2. Check session storage (current session only)
    const sessionDebug = sessionStorage.getItem('nudgesmart_debug');
    if (sessionDebug === 'true') return true;
    
    // 3. Only in dev: check URL parameter for temporary debugging
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('nudgesmart_debug') === 'true') {
      // Auto-save to session for this session
      sessionStorage.setItem('nudgesmart_debug', 'true');
      return true;
    }
    
    return false;
  } catch (e) {
    // If storage access fails, default to false
    console.warn('[Nudgesmart] Could not access storage for debug mode:', e);
    return false;
  }
})();

// Secure console management with environment awareness
(() => {
  // Only modify console in production
  if (import.meta.env.PROD) {
    // Store originals in closure (not globally accessible)
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
      trace: console.trace,
      table: console.table,
      group: console.group,
      groupEnd: console.groupEnd
    };
    
    // Create no-op functions
    const noop = () => {};
    
    // Override console methods
    console.log = noop;
    console.info = noop;
    console.debug = noop;
    console.trace = noop;
    console.table = noop;
    console.group = noop;
    console.groupEnd = noop;
    
    // Keep warn for important warnings (with filtering)
    console.warn = (...args) => {
      // Only log warnings that start with [Nudgesmart]
      if (args[0]?.toString().startsWith('[Nudgesmart]')) {
        originalConsole.warn(...args);
      }
    };
    
    // Keep error but sanitize sensitive data
    console.error = (...args) => {
      // Always log errors but sanitize
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          // Remove potential sensitive data
          return arg
            .replace(/[\w\.-]+@[\w\.-]+\.\w+/g, '[EMAIL]')
            .replace(/\b[A-Za-z0-9]{32,}\b/g, '[KEY]');
        }
        return arg;
      });
      originalConsole.error('[PRODUCTION]', ...sanitizedArgs);
    };
    
    // No global debug functions in production
    // window.restoreConsole NOT defined
    // window.isConsoleDisabled NOT defined
    
  } else {
    // Development environment - add helpful debug utilities
    window.__nudgesmart_dev = {
      version: '1.0.0',
      env: import.meta.env.MODE,
      debug: {
        enableConsole: () => {
          localStorage.setItem('nudgesmart_debug', 'true');
          console.log('[Nudgesmart Dev] Debug mode enabled');
        },
        disableConsole: () => {
          localStorage.setItem('nudgesmart_debug', 'false');
          console.log('[Nudgesmart Dev] Debug mode disabled');
        },
        status: () => ({
          debugMode: isDebugForced,
          environment: import.meta.env.MODE,
          timestamp: new Date().toISOString()
        })
      }
    };
    
    console.log(
      '%c[Nudgesmart Dev] Development mode active',
      'color: #00dc82; font-weight: bold;',
      '\nDebug utilities available at window.__nudgesmart_dev'
    );
  }
})();

// Initialize js-confetti globally for Nudgesmart
if (typeof window !== 'undefined' && !window.JSConfetti) {
  window.JSConfetti = JSConfetti;
  console.log('✅ JSConfetti initialized globally for Nudgesmart');
}

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found for booting react app");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <AppProvider i18n={enTranslations}>
      <App />
    </AppProvider>
  </React.StrictMode>
);
